"use client";

import Link from "next/link";
import {
  Compass,
  Mail,
  Phone,
  MapPin,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocaleRouter } from "@/hooks/useLocaleRouter";
import { Title } from "@/components/ui/Headings";

const Footer = () => {
  const { t } = useTranslation();
  const { changeLocale, createLocalizedPath, currentLocale } =
    useLocaleRouter();

  const setLang = (lng: "en" | "de") => {
    changeLocale(lng);
  };
  return (
    <footer className="bg-primary-700 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Brand */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <Compass className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              <span className="text-xl sm:text-2xl font-serif font-semibold">
                {t("brand")}
              </span>
            </div>
            <p className="text-white-400 mb-6 max-w-md text-xs sm:text-sm">
              {t("hero.titleLine1")} {t("hero.titleLine2")} {t("hero.subtitle")}
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <a
                href="https://www.instagram.com/revoyager.travel/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-primary-800"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?viewas=100000686899395&id=61578857017148"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-primary-800"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/revoyager/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-primary-800"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com/@Revoyager.travel"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-primary-800"
              >
                <Youtube className="h-5 w-5" />
              </a>
              <a
                href="mailto:hello@revoyager.com"
                className="text-gray-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-primary-800"
              >
                <Mail className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <Title size="xs" className="font-semibold mb-3 text-white">
              {t("footer.quickLinks")}
            </Title>
            <ul className="space-y-1">
              <li>
                <Link
                  href={createLocalizedPath("/about")}
                  className="text-white-300 hover:text-white transition-colors text-sm block py-0.5"
                >
                  {t("nav.about")}
                </Link>
              </li>
              <li>
                <Link
                  href={createLocalizedPath("/signature-journeys")}
                  className="text-white-300 hover:text-white transition-colors text-sm block py-0.5"
                >
                  {t("nav.signatureJourneys")}
                </Link>
              </li>
              <li>
                <Link
                  href={createLocalizedPath("/travel-themes")}
                  className="text-white-400 hover:text-white transition-colors text-sm block py-0.5"
                >
                  {t("nav.travelThemes")}
                </Link>
              </li>
              <li>
                <Link
                  href={createLocalizedPath("/travel-circle")}
                  className="text-white-300 hover:text-white transition-colors text-sm block py-0.5"
                >
                  {t("nav.travelCircle")}
                </Link>
              </li>
              <li>
                <Link
                  href={createLocalizedPath("/journal")}
                  className="text-white-300 hover:text-white transition-colors text-sm block py-0.5"
                >
                  {t("nav.journal")}
                </Link>
              </li>
              <li>
                <Link
                  href={createLocalizedPath("/support")}
                  className="text-white-300 hover:text-white transition-colors text-sm block py-0.5"
                >
                  {t("nav.support")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <Title size="xs" className="font-semibold mb-4 text-white">
              {t("footer.contact")}
            </Title>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="h-4 w-4 text-white-400 flex-shrink-0" />
                <span className="text-white-300 text-sm sm:text-base break-all">
                  hello@revoyager.com
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-4 w-4 text-white-400 flex-shrink-0" />
                <a
                  href="tel:+491785484825"
                  className="text-white-300 hover:text-white transition-colors text-sm sm:text-base"
                >
                  +49 1785484825
                </a>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="h-4 w-4 text-white-400 flex-shrink-0 mt-0.5" />
                <span className="text-white-300 text-sm sm:text-base">
                  {t("footer.location")}
                </span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <Link
                href={createLocalizedPath("/contact")}
                className="inline-block text-white px-4 py-2 rounded-full text-sm font-medium transition-colors w-full sm:w-auto text-center"
                style={{ backgroundColor: "#cdc50a" }}
              >
                {t("footer.getInTouch")}
              </Link>
              <a
                href="https://calendly.com/lesia-revoyager/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white text-primary-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors w-full sm:w-auto text-center"
              >
                Book a Free Call
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-primary-700 pt-3 sm:pt-4 mt-3 sm:mt-4">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
            <p className="text-white-400 text-xs sm:text-sm text-center sm:text-left">
              {t("footer.copyright")}
            </p>
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-6 text-xs sm:text-sm">
              <div className="flex flex-wrap justify-center sm:justify-start items-center gap-3 sm:gap-6">
                <Link
                  href={createLocalizedPath("/privacy")}
                  className="text-white-400 hover:text-white transition-colors py-1"
                >
                  {t("footer.privacy")}
                </Link>
                <Link
                  href={createLocalizedPath("/cookie-settings")}
                  className="text-white-400 hover:text-white transition-colors py-1"
                >
                  Cookie Settings
                </Link>
                <Link
                  href={createLocalizedPath("/agb")}
                  className="text-white-400 hover:text-white transition-colors py-1"
                >
                  {t("footer.terms")}
                </Link>
                <Link
                  href={createLocalizedPath("/impressum")}
                  className="text-white-400 hover:text-white transition-colors py-1"
                >
                  {t("footer.impressum")}
                </Link>
              </div>
              <div className="inline-flex items-center rounded-full border border-primary-700 overflow-hidden">
                <button
                  onClick={() => setLang("en")}
                  className={`px-3 py-2 text-xs font-medium min-w-[40px] ${
                    currentLocale === "en"
                      ? "bg-primary-700 text-white"
                      : "text-gray-200 hover:bg-primary-800"
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLang("de")}
                  className={`px-3 py-2 text-xs font-medium min-w-[40px] ${
                    currentLocale === "de"
                      ? "bg-primary-700 text-white"
                      : "text-gray-200 hover:bg-primary-800"
                  }`}
                >
                  DE
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
